import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { Mail, Phone, MapPin } from "lucide-react";

export default function Contact() {
  const { t } = useI18n();
  const [site, setSite] = useState({});
  const [f, setF] = useState({ name:"", email:"", phone:"", subject:"", message:"" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/settings").then(r => setSite(r.data?.site || {})).catch(() => {});
    const handler = () => api.get("/settings").then(r => setSite(r.data?.site || {})).catch(() => {});
    window.addEventListener("lel-settings-updated", handler);
    return () => window.removeEventListener("lel-settings-updated", handler);
  }, []);

  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", f);
      toast.success(t("contact.thanks"));
      setF({ name:"", email:"", phone:"", subject:"", message:"" });
    } catch { toast.error(t("contact.err")); }
    finally { setLoading(false); }
  };

  const contactEmail = site.contact_email || "contact@larmes-en-lumiere.org";
  const hasPhone = site.contact_phone && site.contact_phone !== "À compléter";
  const hasAddress = site.address && site.address !== "À compléter";

  return (
    <div data-testid="contact-page">
      <section className="hero-gradient py-24">
        <div className="max-w-6xl mx-auto px-5 lg:px-10">
          <div className="text-xs uppercase tracking-[0.3em] text-warm-orange font-semibold">{t("pages.contact.tag")}</div>
          <h1 className="font-display text-deep-blue text-5xl sm:text-6xl lg:text-7xl leading-tight mt-3">{t("pages.contact.t1")}<br/><span className="italic text-warm-orange">{t("pages.contact.t2")}</span></h1>
        </div>
      </section>
      <section className="py-16 bg-ivory">
        <div className="max-w-6xl mx-auto px-5 lg:px-10 grid lg:grid-cols-3 gap-8">
          <div className="space-y-4 lg:col-span-1">
            <a href={`mailto:${contactEmail}`} className="block p-6 rounded-2xl bg-deep-blue text-ivory hover:bg-deep-blue/90 transition" data-testid="contact-info-email">
              <Mail className="text-gold mb-3"/>
              <div className="text-xs uppercase tracking-widest text-gold">{t("contact.emailLabel")}</div>
              <div className="mt-1 break-all">{contactEmail}</div>
            </a>
            {hasPhone ? (
              <a href={`tel:${site.contact_phone.replace(/\s/g,'')}`} className="block p-6 rounded-2xl bg-pearl hover:bg-pearl/70 transition" data-testid="contact-info-phone">
                <Phone className="text-warm-orange mb-3"/>
                <div className="text-xs uppercase tracking-widest text-warm-orange">{t("contact.phoneLabel")}</div>
                <div className="mt-1 text-deep-blue font-semibold">{site.contact_phone}</div>
              </a>
            ) : (
              <div className="p-6 rounded-2xl bg-pearl" data-testid="contact-info-phone">
                <Phone className="text-warm-orange mb-3"/>
                <div className="text-xs uppercase tracking-widest text-warm-orange">{t("contact.phoneLabel")}</div>
                <div className="mt-1 text-deep-blue italic">{t("contact.empty")}</div>
              </div>
            )}
            <div className="p-6 rounded-2xl bg-pearl" data-testid="contact-info-address">
              <MapPin className="text-green-soft mb-3"/>
              <div className="text-xs uppercase tracking-widest text-green-soft">{t("contact.addressLabel")}</div>
              <div className={`mt-1 ${hasAddress ? "text-deep-blue" : "text-deep-blue italic"}`}>{hasAddress ? site.address : t("contact.empty")}</div>
            </div>
          </div>
          <form onSubmit={submit} className="lg:col-span-2 grid md:grid-cols-2 gap-4 p-8 rounded-2xl bg-white border border-black/5">
            <input required placeholder={t("common.name")} className="p-4 rounded-xl border-2 border-black/5 focus:border-gold outline-none" value={f.name} onChange={e=>set("name",e.target.value)} data-testid="c-name"/>
            <input required type="email" placeholder={t("common.email")} className="p-4 rounded-xl border-2 border-black/5 focus:border-gold outline-none" value={f.email} onChange={e=>set("email",e.target.value)} data-testid="c-email"/>
            <input placeholder={t("common.phone")} className="p-4 rounded-xl border-2 border-black/5 focus:border-gold outline-none" value={f.phone} onChange={e=>set("phone",e.target.value)} data-testid="c-phone"/>
            <input placeholder={t("common.subject")} className="p-4 rounded-xl border-2 border-black/5 focus:border-gold outline-none" value={f.subject} onChange={e=>set("subject",e.target.value)} data-testid="c-subject"/>
            <textarea required rows={5} placeholder={t("common.message")} className="md:col-span-2 p-4 rounded-xl border-2 border-black/5 focus:border-gold outline-none" value={f.message} onChange={e=>set("message",e.target.value)} data-testid="c-message"/>
            <button disabled={loading} className="btn-gold md:col-span-2 py-4 rounded-full font-semibold" data-testid="c-submit">{loading?t("common.sending"):t("common.send")}</button>
          </form>
        </div>
      </section>
    </div>
  );
}
